// Role checking utilities for consistent access control

/**
 * Check if a user has a specific role (case insensitive, word-based)
 * @param user - The user object
 * @param roleName - The role to check for (e.g., "admin", "tutor", "student")
 * @returns boolean indicating if user has the role
 */
export function hasRole(user: any, roleName: string): boolean {
  if (!user) return false;
  
  const targetRole = roleName.toLowerCase();
  
  // Check if user.role contains the target role (case insensitive)
  if (user.role && typeof user.role === 'string' && user.role.toLowerCase().includes(targetRole)) {
    return true;
  }
  
  // Check if user.roles array contains the target role (case insensitive)
  if (user.roles && Array.isArray(user.roles)) {
    return user.roles.some((role: any) => 
      role && typeof role === 'string' && role.toLowerCase().includes(targetRole)
    );
  }
  
  return false;
}

/**
 * Check if user is an admin
 */
export function isAdmin(user: any): boolean {
  return hasRole(user, 'admin');
}

/**
 * Check if user is a tutor
 */
export function isTutor(user: any): boolean {
  return hasRole(user, 'tutor');
}

/**
 * Check if user is a student
 */
export function isStudent(user: any): boolean {
  return hasRole(user, 'student');
}

/**
 * Check if user is an advertiser
 */
export function isAdvertiser(user: any): boolean {
  return hasRole(user, 'advertiser');
}

/**
 * Check if user is an employee
 */
export function isEmployee(user: any): boolean {
  return hasRole(user, 'employee');
}

/**
 * Check if user is a candidate
 */
export function isCandidate(user: any): boolean {
  return hasRole(user, 'candidate');
}

/**
 * Get the primary role of a user (for display purposes)
 */
export function getPrimaryRole(user: any): string {
  if (!user) return 'guest';
  
  // Priority order for role display
  const rolePriority = ['admin', 'employee', 'advertiser', 'tutor', 'student', 'candidate'];
  
  for (const role of rolePriority) {
    if (hasRole(user, role)) {
      return role;
    }
  }
  
  return user.role || 'user';
} 